<template>
  <div class="overflow-x-hidden bg-root-6">
    <div class="md:mx-14 mx-4">
      <div v-if="dashboardLoading">
        <dashboard-loader />
      </div>
      <div v-else>
        <top-section @openDrawer="openDrawer" />
        <div
          v-if="tenantPermission('display-dashboard-exchange-rates')"
          class="grid lg:gap-6 gap-y-4 xl:grid-cols-12 grid-cols-1"
        >
          <!-- daily rates here-->
          <div class="col-span-6">
            <daily-rates @showExchangeRates="showExchangeRates" />
          </div>
          <!-- transactions here-->
          <div class="col-span-6">
            <transactions-list @showTransaction="showTransaction" />
          </div>
        </div>
        <div v-else>
          <holding-transactions use="dashboard" />
        </div>
      </div>
    </div>
    <!-- drawer -->
    <dashboard-drawer
      v-if="showDashboardDrawer"
      :showDrawer="showDashboardDrawer"
      @closeDrawer="closeDrawer('showDashboardDrawer')"
      :drawerContent="drawerContent"
    />
    <transaction-drawer
      v-if="showTransactionDrawer"
      :showDrawer="showTransactionDrawer"
      @closeDrawer="closeDrawer('showTransactionDrawer')"
      :transactionDetails="currentTransaction"
      :transactionType="currentTransaction.type"
      @showDepositDetails="showDepositDetails"
      @showDepositType="showDepositType"
    />
    <initiate-tour-button @startTour="startDashboardTour" />
    <dashboard-tour-welcome-dialog
      @startDashboardTour="startDashboardTour"
      v-if="screenIsNotMobile"
    />
    <app-tour
      :tourDetails="dashboardTour"
      tourComponent="dashboard"
      :displayTour="displayTour"
      @stopTour="stopTour"
    />
  </div>
</template>

<script>
import { DASHBOARD_TOUR } from '@/utils/constants'
import getKycDetails from '@/mixins/kyc.js'
import breakpoints from '@/utils/breakpoints.js'
import pendingDeposit from '@/mixins/pendingDeposit.js'
import getBalance from '@/mixins/getBalance.js'
import eventBus from '@/global/eventBus'
import logout from '@/mixins/logout.js'
import reports from '@/mixins/getDashboardReports.js'
import { expectedInterestSeconds } from '@/utils/helpers.js'
import localDeposit from '@/mixins/localDeposit.js'
import transactions from '@/mixins/transactions.js'
import currencyUpdates from '@/mixins/currencyUpdates.js'
import getSwapPairsAssetsDetails from '@/mixins/bridge.js'
import tenant from '@/mixins/tenant.js'
import virtualAccounts from '@/mixins/virtualAccounts.js'
import notification from '@/mixins/notification.js'

export default {
  mixins: [
    pendingDeposit,
    localDeposit,
    getBalance,
    reports,
    logout,
    transactions,
    currencyUpdates,
    getKycDetails,
    getSwapPairsAssetsDetails,
    tenant,
    virtualAccounts,
    notification
  ],
  name: 'Dashboard',
  data () {
    return {
      displayTour: false,
      dashboardTour: DASHBOARD_TOUR,
      showDashboardDrawer: false,
      showTransactionDrawer: false,
      drawerContent: '',
      showDepositPendingDialog: false,
      showTransactionDialog: false,
      currentTransaction: {},
      showInterestCalculatorDialog: false,
      interestInterval: null,
      updatedBalance: 0,
      breakpoints,
      expectedInterest: 0,
      isLoadingTransactions: false,
      loadingReports: false
    }
  },
  methods: {
    startDashboardTour () {
      this.$tours.dashboardTour.start()
      this.displayTour = true
    },
    stopTour () {
      this.displayTour = false
    },
    showDepositDetails (transactionMethod) {
      if (transactionMethod === this.$transactionType.LOCAL_TRANSFER) {
        this.$store.dispatch(
          'transactions/setDepositTypeView',
          'localAccountInformation'
        )
      } else if (transactionMethod === this.$transactionType.ASSET_TRANSFER) {
        this.$store.dispatch(
          'transactions/setDepositTypeView',
          'assetAccountInformation'
        )
      } else {
        this.$store.dispatch(
          'transactions/setDepositTypeView',
          'accountInformation'
        )
      }
      eventBus.$emit('depositDetailsEvent')
    },
    showDepositType () {
      eventBus.$emit('depositType')
    },
    showTransaction (transaction) {
      this.$store.dispatch('transactions/setCurrentTransaction', transaction)
      this.currentTransaction = transaction
      this.showTransactionDrawer = true
    },
    proceedToWithdraw () {
      if (!this.withdrawalAccess.includes(this.user.kind)) {
        this.$message({
          showClose: true,
          message: 'No withdrawal access',
          type: 'info'
        })
        return
      }
      this.$router.push('withdrawal')
    },
    goToDeposit () {
      if (this.hasPendingDeposit && this.pendingDepositDetails) {
        this.showDepositPendingDialog = true
      } else {
        this.proceedToDeposit()
      }
    },
    closeDepositAmountDialog () {
      this.showDepositAmountDialog = false
    },
    closeWalletDialog () {
      this.showWalletDialog = false
    },
    closeEditPendingDepositDialog () {
      this.showEditPendingDepositDialog = false
    },
    closeDepositTypeDialog () {
      this.showDepositTypeDialog = false
    },
    closePendingDepositDialog () {
      this.showDepositPendingDialog = false
    },
    getNewBalance () {
      this.updatedBalance = expectedInterestSeconds(
        this.apyValue,
        this.totalBalance
      )
    },
    openDrawer (drawerContent) {
      this.drawerContent = drawerContent
      this.showDashboardDrawer = true
    },
    showExchangeRates (content) {
      this.drawerContent = content
      this.showDashboardDrawer = true
    },
    closeDrawer (arg) {
      this[arg] = false
    }
  },
  components: {
    AppTour: () => import('@/components/v2/app-tour/AppTour.vue'),
    DashboardTourWelcomeDialog: () =>
      import('@/components/v2/app-tour/DashboardTourWelcomeDialog.vue'),
    TopSection: () => import('@/components/v2/dashboard/TopSection.vue'),
    DailyRates: () => import('@/components/v2/dashboard/DailyRates.vue'),
    TransactionsList: () =>
      import('@/components/v2/dashboard/TransactionsList.vue'),
    DashboardDrawer: () =>
      import('@/components/v2/dashboard/DashboardDrawer.vue'),
    HoldingTransactions: () =>
      import('@/components/v2/transactions/layout/HoldingTransactions.vue'),
    TransactionDrawer: () =>
      import(
        '@/components/v2/transactions/drawers/transactionDrawer/Index.vue'
      ),
    InitiateTourButton: () =>
      import('@/components/v2/app-tour/InitiateTourButton.vue'),
    DashboardLoader: () =>
      import('@/components/v2/skeletalLoaders/DashboardLoader.vue')
  },
  computed: {
    screenIsNotMobile () {
      return breakpoints.lgAndUp
    },
    dashboardLoading () {
      return this.loadingExchangeRates || this.loadingReports
    },
    hasCompletedKyc () {
      return this.$store.state.auth.business.completedKyc
    },
    payload () {
      return this.$store.state.report.currentCurrencyPair
    },
    newBalance () {
      return this.updatedBalance
    },
    user () {
      return this.$store.state.auth.user
    },
    isbusinessAccountActive () {
      return this.$store.state.auth.business.accountActive
    },
    agreementContract () {
      return this.$store.state.transactions.depositContract
    }
  },
  async beforeMount () {
    await this.getDashboardReports()
    await this.getCurrencyUpdates()
  },
  async mounted () {
    if (this.hasCompletedKyc) {
      await this.getTransferDetails()
    }
    await this.getWalletAddresses()
    this.getAllNotifications()
  }
}
</script>

<style scoped scss>
.text-root-2 {
  color: var(--text-2);
}

.text-root-1 {
  color: var(--text-1);
}

.text-bg-root-1 {
  color: var(--background);
}

.rounded-40px {
  border-radius: 40px;
}

.rounded-30px {
  border-radius: 30px;
}

.bg-root {
  background: var(--background);
}

.bg-root-1 {
  background: var(--background-1);
}

.bg-root-6 {
  background: var(--background-6);
}

.bg-root-2 {
  background: var(--background-2);
}

.dashboard-card {
  background: var(--background);
  box-shadow:
    0px 0px 1px rgba(3, 2, 7, 0.1),
    0px 4px 8px rgba(3, 2, 7, 0.1);
  border-radius: 15px;
}

.card-shadow {
  box-shadow: 0 2px 12px 0 rgb(0 0 0 / 10%);
}

.border-bottom {
  border-bottom: 1.5px solid #dddddd;
}

.button-border {
  border: 0.5px solid var(--text-3);
}

>>> .el-select .el-input .el-select__caret {
  color: var(--color-primary);
}

>>> .el-input__inner {
  border: none;
  background: var(--background);
  color: #9e9e9e;
}
</style>
